// Parametric vase generator — produces a triangle mesh from a profile of radii sampled
// along Z. The vase is a surface of revolution: at each "ring" Z height we have a base
// radius modulated by a profile function, then sampled into N segments around the axis.

import type { Triangle, Vec3 } from './types';
import { tessellatePath, crossSectionLUT, sampleCrossSection, type PenPath } from './penTool';

/** Optional grayscale image grid used to displace vase surface (texture-from-image). */
export interface TextureGrid {
  w: number;
  h: number;
  data: Float32Array; // luminance 0..1, row-major (y * w + x)
  intensity: number;  // mm of radial offset at full white. NEGATIVE = push inward (carve).
  repeatU: number;    // tiles around the axis
  repeatV: number;    // tiles up the height
  /** Optional Z-band masking: texture only applies between zMin..zMax (normalized 0..1
   *  along the vase height). Default zMin=0, zMax=1 → applies everywhere. */
  zMin?: number;
  zMax?: number;
  /** Smooth fade-in band sizes at the bottom/top edges of [zMin, zMax] (normalized).
   *  Default 0.02 each → ~2% height crossfade. Set to 0 for hard edges. */
  falloffBottom?: number;
  falloffTop?: number;
  /** Falloff curve shape exponent: 1=linear smoothstep, >1 sharper, <1 softer. Default 1. */
  falloffCurve?: number;
  /** Surface smoothing — overrides shader's RAMP constant (default 5.0 px).
   *  Larger = wider/softer transition wall, eliminates faceting at extreme intensities. */
  surfaceSmoothing?: number;
}

/** A single control point on the vase silhouette curve (z = height in mm, r = radius in mm). */
export interface ProfilePoint {
  z: number;
  r: number;
}

export interface VaseParams {
  segments: number;
  rings: number;
  height: number;
  bottomRadius: number;
  topRadius: number;
  bellyRadius: number;
  bellyHeight: number;
  twist: number;
  wallThickness: number;
  bottomThickness: number;
  noiseAmplitude: number;
  noiseFrequency: number;
  texture?: TextureGrid | null;
  /** When set (and length >= 2), profilePoints OVERRIDE the legacy 3-point model
   *  (bottomRadius/bellyRadius/topRadius/bellyHeight). The mesh height is taken
   *  from the largest z in the array — `height` field is ignored in that mode. */
  profilePoints?: ProfilePoint[];
  /** Pen-tool profile path (line/quad/cubic/arc anchors). When set, supersedes
   *  profilePoints — `effectiveProfile()` tessellates this into a flat ProfilePoint[]
   *  before all downstream consumers (mesh build, GPU shader, slicer) read it. */
  penProfile?: PenPath | null;
  /** Pen-tool cross-section path (closed). Phase 2 — when set, the surface of
   *  revolution is replaced by a swept cross-section. */
  penCrossSection?: PenPath | null;
  /** Base body color (hex int). Default = clay 0xc87b3a. */
  baseColor?: number;
  /** Accent color used in texture-displaced (raised) areas. Default = white. */
  accentColor?: number;
}

export const DEFAULT_VASE: VaseParams = {
  segments: 96,
  rings: 80,
  height: 120,
  bottomRadius: 35,
  topRadius: 30,
  bellyRadius: 50,
  bellyHeight: 0.45,
  twist: 0,
  wallThickness: 2.5,
  bottomThickness: 3,
  noiseAmplitude: 0,
  noiseFrequency: 6,
  texture: null,
  baseColor: 0xc87b3a,
  accentColor: 0xffffff
};

/** Tessellate a Pen profile path into legacy ProfilePoint[] (sorted by z, deduplicated).
 *  Returns null when path is missing or has fewer than 2 anchors. */
export function tessellatePenProfile(path: PenPath | null | undefined): ProfilePoint[] | null {
  if (!path || path.anchors.length < 2) return null;
  const poly = tessellatePath(path, 96);
  if (poly.length < 2) return null;
  return poly.map((p) => ({ z: p.z, r: Math.max(0.5, p.r) }));
}

/** Resolve the effective ProfilePoint[] for downstream consumers. Order:
 *  1) penProfile (if valid → tessellated) — overrides everything
 *  2) profilePoints (legacy explicit array)
 *  3) null (caller falls back to bottom/belly/top + bellyHeight curve) */
export function effectiveProfile(p: VaseParams): ProfilePoint[] | null {
  if (p.penProfile) {
    const tess = tessellatePenProfile(p.penProfile);
    if (tess) return tess;
  }
  if (p.profilePoints && p.profilePoints.length >= 2) return p.profilePoints;
  return null;
}

/** Built-in vase preset shapes — all values copied into the active params. */
export const VASE_PRESETS: { id: string; name: string; params: Partial<VaseParams> }[] = [
  { id: 'classic', name: 'Klasik Vazo', params: { height: 120, bottomRadius: 35, topRadius: 30, bellyRadius: 50, bellyHeight: 0.45, twist: 0, noiseAmplitude: 0 } },
  { id: 'cylinder', name: 'Silindir', params: { height: 100, bottomRadius: 35, topRadius: 35, bellyRadius: 35, bellyHeight: 0.5, twist: 0, noiseAmplitude: 0 } },
  { id: 'cone', name: 'Konik Saksı', params: { height: 90, bottomRadius: 25, topRadius: 50, bellyRadius: 38, bellyHeight: 0.5, twist: 0, noiseAmplitude: 0 } },
  { id: 'bowl', name: 'Kase', params: { height: 50, bottomRadius: 30, topRadius: 60, bellyRadius: 50, bellyHeight: 0.7, twist: 0, noiseAmplitude: 0 } },
  { id: 'mug', name: 'Kupa', params: { height: 90, bottomRadius: 35, topRadius: 36, bellyRadius: 36, bellyHeight: 0.5, twist: 0, noiseAmplitude: 0, wallThickness: 3.5 } },
  { id: 'hourglass', name: 'Kum Saati', params: { height: 140, bottomRadius: 45, topRadius: 45, bellyRadius: 18, bellyHeight: 0.5, twist: 0, noiseAmplitude: 0 } },
  { id: 'twisted', name: 'Burgu', params: { height: 150, bottomRadius: 30, topRadius: 30, bellyRadius: 35, bellyHeight: 0.5, twist: 360, noiseAmplitude: 1.2, noiseFrequency: 8 } },
  { id: 'rippled', name: 'Dalgalı', params: { height: 150, bottomRadius: 30, topRadius: 30, bellyRadius: 45, bellyHeight: 0.5, twist: 0, noiseAmplitude: 4, noiseFrequency: 14 } },
  { id: 'pitcher', name: 'Sürahi', params: { height: 180, bottomRadius: 40, topRadius: 22, bellyRadius: 55, bellyHeight: 0.35, twist: 0, noiseAmplitude: 0 } }
];

/** Effective height — derived from effectiveProfile max-z when present, else legacy `height`. */
export function effectiveHeight(p: VaseParams): number {
  const prof = effectiveProfile(p);
  if (prof) {
    let maxZ = -Infinity;
    for (const pt of prof) if (pt.z > maxZ) maxZ = pt.z;
    return Math.max(1, maxZ);
  }
  return p.height;
}

/** Linear interpolation through a sorted-by-Z list of profile points. */
function interpProfile(pts: ProfilePoint[], z: number): number {
  // Sort copy to be safe (caller usually maintains order, but defensive).
  const sorted = pts.slice().sort((a, b) => a.z - b.z);
  if (z <= sorted[0].z) return sorted[0].r;
  if (z >= sorted[sorted.length - 1].z) return sorted[sorted.length - 1].r;
  for (let i = 0; i < sorted.length - 1; i++) {
    const a = sorted[i], b = sorted[i + 1];
    if (z >= a.z && z <= b.z) {
      const t = (z - a.z) / Math.max(1e-6, b.z - a.z);
      return a.r + (b.r - a.r) * t;
    }
  }
  return sorted[sorted.length - 1].r;
}

/** Smooth radius interpolation: bottom -> belly -> top using a quadratic blend.
 *  When `effectiveProfile()` returns non-null, falls through to piecewise-linear
 *  interpolation (consumes pen-tessellated or legacy profilePoints transparently). */
function radiusAt(t: number, p: VaseParams): number {
  const prof = effectiveProfile(p);
  if (prof) {
    const z = t * effectiveHeight(p);
    let r = interpProfile(prof, z);
    if (p.noiseAmplitude > 0) {
      r += Math.sin(t * Math.PI * p.noiseFrequency) * p.noiseAmplitude * 0.5;
    }
    return Math.max(0.5, r);
  }
  const bh = Math.max(0.01, Math.min(0.99, p.bellyHeight));
  let r: number;
  if (t <= bh) {
    const u = t / bh;
    const e = u * u * (3 - 2 * u);
    r = p.bottomRadius + (p.bellyRadius - p.bottomRadius) * e;
  } else {
    const u = (t - bh) / (1 - bh);
    const e = u * u * (3 - 2 * u);
    r = p.bellyRadius + (p.topRadius - p.bellyRadius) * e;
  }
  if (p.noiseAmplitude > 0) {
    r += Math.sin(t * Math.PI * p.noiseFrequency) * p.noiseAmplitude * 0.5;
  }
  return Math.max(0.5, r);
}

/** Sample texture grid (signed distance field, pixel units) at u,v in [0..1].
 *  Positive = inside the SVG drawing, negative = outside. Wrapped via fract. */
function sampleTexture(tex: TextureGrid, u: number, v: number): number {
  let uu = ((u * tex.repeatU) % 1 + 1) % 1;
  let vv = ((v * tex.repeatV) % 1 + 1) % 1;
  const x = Math.min(tex.w - 1, Math.floor(uu * tex.w));
  const y = Math.min(tex.h - 1, Math.floor(vv * tex.h));
  return tex.data[y * tex.w + x];
}

/** Build the outer skin as a surface of revolution. */
function buildSkin(p: VaseParams, radiusOffset = 0): Vec3[][] {
  const grid: Vec3[][] = [];
  const seg = Math.max(3, Math.floor(p.segments));
  const rings = Math.max(2, Math.floor(p.rings));
  const twistRad = (p.twist * Math.PI) / 180;
  const H = effectiveHeight(p);
  const csLut = crossSectionLUT(p.penCrossSection ?? null, 64);
  for (let i = 0; i <= rings; i++) {
    const t = i / rings;
    const z = t * H;
    const baseR = Math.max(0.1, radiusAt(t, p) + radiusOffset);
    const phi = twistRad * t;
    const ring: Vec3[] = [];
    for (let j = 0; j < seg; j++) {
      const u = j / seg;
      const a = u * Math.PI * 2 + phi;
      let rr = baseR;
      if (p.noiseAmplitude > 0) {
        rr += Math.sin(a * p.noiseFrequency) * p.noiseAmplitude * 0.5;
      }
      // Texture displacement (only on outer skin: positive offset = bumps outward)
      if (p.texture && radiusOffset === 0) {
        const tex = p.texture;
        const sdf = sampleTexture(tex, u, t);
        // Match shader: linear ramp clamp(sdf/RAMP, -1, 1) — flat top, uniform slope.
        const ramp = Math.max(-1, Math.min(1, sdf / Math.max(0.5, tex.surfaceSmoothing ?? 5)));
        // Z-band masking with smoothstep falloffs.
        const zMin = tex.zMin ?? 0;
        const zMax = tex.zMax ?? 1;
        const fb = Math.max(0, tex.falloffBottom ?? 0.02);
        const ft = Math.max(0, tex.falloffTop ?? 0.02);
        const curve = Math.max(0.1, tex.falloffCurve ?? 1);
        let band = 1;
        if (t < zMin || t > zMax) band = 0;
        else {
          const inLo = fb > 0 ? Math.min(1, Math.max(0, (t - zMin) / fb)) : 1;
          const inHi = ft > 0 ? Math.min(1, Math.max(0, (zMax - t) / ft)) : 1;
          const sLo = inLo * inLo * (3 - 2 * inLo);
          const sHi = inHi * inHi * (3 - 2 * inHi);
          band = Math.pow(Math.min(sLo, sHi), curve);
        }
        rr += ramp * tex.intensity * band;
      }
      // Cross-section radial scale (1.0 = circular when no LUT).
      const csScale = csLut ? sampleCrossSection(csLut, a) : 1;
      ring.push([Math.cos(a) * rr * csScale, Math.sin(a) * rr * csScale, z]);
    }
    grid.push(ring);
  }
  return grid;
}

/** Triangulate two adjacent rings of the same segment count. */
function stitchRings(a: Vec3[], b: Vec3[], outwardNormal: 1 | -1, out: Triangle[]): void {
  const n = a.length;
  for (let j = 0; j < n; j++) {
    const j1 = (j + 1) % n;
    const A = a[j], B = a[j1], C = b[j1], D = b[j];
    // Quad ABCD split into two triangles. Winding flipped if outwardNormal = -1 (inside surface).
    if (outwardNormal === 1) {
      out.push(makeTri(A, B, C));
      out.push(makeTri(A, C, D));
    } else {
      out.push(makeTri(A, C, B));
      out.push(makeTri(A, D, C));
    }
  }
}

function makeTri(a: Vec3, b: Vec3, c: Vec3): Triangle {
  const ux = b[0] - a[0], uy = b[1] - a[1], uz = b[2] - a[2];
  const vx = c[0] - a[0], vy = c[1] - a[1], vz = c[2] - a[2];
  const nx = uy * vz - uz * vy;
  const ny = uz * vx - ux * vz;
  const nz = ux * vy - uy * vx;
  const l = Math.hypot(nx, ny, nz) || 1;
  return { a, b, c, normal: [nx / l, ny / l, nz / l] };
}

/**
 * Build the vase as a closed solid mesh (outer skin + inner skin + bottom cap + top rim).
 * If wallThickness=0, returns just the outer skin (open shell — useful for vase-mode preview).
 */
export function buildVaseMesh(params: Partial<VaseParams> = {}): Triangle[] {
  const p: VaseParams = { ...DEFAULT_VASE, ...params };
  const tris: Triangle[] = [];
  const outer = buildSkin(p, 0);

  // Outer skin
  for (let i = 0; i < outer.length - 1; i++) {
    stitchRings(outer[i], outer[i + 1], 1, tris);
  }

  if (p.wallThickness <= 0) {
    // Cap the bottom only (for hollow shell, vase-mode-friendly)
    capRingFlat(outer[0], 0, tris, 1);
    return tris;
  }

  // Inner skin (offset inward), bottom raised by bottomThickness so the floor is solid
  const inner = buildSkin(p, -p.wallThickness);
  // Find the ring index where z >= bottomThickness — inner mesh starts there
  let startInner = 0;
  for (let i = 0; i < inner.length; i++) {
    if (inner[i][0][2] >= p.bottomThickness) {
      startInner = i;
      break;
    }
  }
  for (let i = startInner; i < inner.length - 1; i++) {
    stitchRings(inner[i], inner[i + 1], -1, tris);
  }

  // Bottom cap (closed disc at z=0): triangulate as fan from center outer[0]
  capRingFlat(outer[0], 0, tris, 1);

  // Top rim: connect outer top ring to inner top ring (both at z = height) — annulus
  capAnnulus(outer[outer.length - 1], inner[inner.length - 1], tris, 1);

  // Inner bottom cap (closed disc at z = bottomThickness) inside the wall
  if (startInner < inner.length) {
    const ring = inner[startInner].map((v): Vec3 => [v[0], v[1], p.bottomThickness]);
    capRingFlat(ring, p.bottomThickness, tris, -1);
  }

  return tris;
}

function capRingFlat(ring: Vec3[], z: number, out: Triangle[], facing: 1 | -1): void {
  const n = ring.length;
  // Center of disc
  let cx = 0, cy = 0;
  for (const r of ring) { cx += r[0]; cy += r[1]; }
  cx /= n; cy /= n;
  const center: Vec3 = [cx, cy, z];
  for (let j = 0; j < n; j++) {
    const j1 = (j + 1) % n;
    const a = ring[j];
    const b = ring[j1];
    if (facing === 1) {
      // Bottom faces -Z; vertices ordered so normal points down
      out.push(makeTri(center, b, a));
    } else {
      out.push(makeTri(center, a, b));
    }
  }
}

function capAnnulus(outerRing: Vec3[], innerRing: Vec3[], out: Triangle[], facing: 1 | -1): void {
  const n = Math.min(outerRing.length, innerRing.length);
  for (let j = 0; j < n; j++) {
    const j1 = (j + 1) % n;
    const A = outerRing[j], B = outerRing[j1];
    const C = innerRing[j1], D = innerRing[j];
    if (facing === 1) {
      out.push(makeTri(A, B, C));
      out.push(makeTri(A, C, D));
    } else {
      out.push(makeTri(A, C, B));
      out.push(makeTri(A, D, C));
    }
  }
}
